/**
 * Typed fetch wrapper.
 *
 * The client — in a browser or the desktop app, which loads the same site —
 * authenticates with an HttpOnly cookie, so every request sends credentials
 * and nothing token-related is ever held in JavaScript.
 */

import { recordFailure, reportError } from "./error-report.js";
import { activeWorkspaceHeader } from "./transport.js";

export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly code?: string,
    /** Fastify's request id (x-request-id), for matching a report to a log line. */
    readonly requestId?: string,
    readonly method?: string,
    readonly path?: string,
  ) {
    super(message);
    this.name = "ApiError";
  }

  /** The session is gone or was never established. */
  get isUnauthenticated() {
    return this.status === 401;
  }

  /** Authenticated, but not allowed to do this. */
  get isForbidden() {
    return this.status === 403;
  }

  /** Someone else changed the record first. */
  get isConflict() {
    return this.status === 409;
  }
}

/** Fired when any request is refused because two-factor enrolment is required. */
export const TWO_FACTOR_REQUIRED_EVENT = "astrum:two-factor-required";

/**
 * Fired when a request is refused because the session behind it is gone —
 * expired, signed out elsewhere, or revoked by an administrator. Only the
 * auth plugin's "no session" refusal carries UNAUTHENTICATED; a wrong
 * password or an expired 2FA challenge does not, so a failed sign-in never
 * fires it.
 */
export const SESSION_ENDED_EVENT = "astrum:session-ended";

type RequestOptions = Omit<RequestInit, "body"> & { body?: unknown };

async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { body, headers, ...rest } = options;

  const response = await fetch(`/api/v1${path}`, {
    ...rest,
    credentials: "include",
    headers: {
      ...(body === undefined ? {} : { "content-type": "application/json" }),
      accept: "application/json",
      ...activeWorkspaceHeader(),
      ...headers,
    },
    ...(body === undefined ? {} : { body: JSON.stringify(body) }),
  });

  if (response.status === 204) return undefined as T;

  const payload = await response.json().catch(() => ({}));

  if (!response.ok) {
    const { error, code } = payload as { error?: string; code?: string };
    const requestId = response.headers.get("x-request-id") ?? (payload as { requestId?: string }).requestId;
    const method = (rest.method ?? "GET").toUpperCase();
    const failure = new ApiError(
      error ?? `Request failed (${response.status})`,
      response.status,
      code,
      requestId ?? undefined,
      method,
      `/api/v1${path}`,
    );
    recordFailure({
      message: failure.message,
      method,
      path: failure.path ?? path,
      status: failure.status,
      requestId: failure.requestId,
      at: new Date(),
    });
    // The workspace has made two-factor mandatory since this session began;
    // the auth provider re-reads the session and the setup screen takes over.
    if (failure.code === "TWO_FACTOR_REQUIRED" && typeof window !== "undefined") {
      window.dispatchEvent(new Event(TWO_FACTOR_REQUIRED_EVENT));
    }
    // The session ended while the app was open (the desktop app sits in the
    // tray for days). Without this every screen stays up and every request
    // fails with "Authentication is required" until the person thinks to
    // sign out and back in.
    if (failure.code === "UNAUTHENTICATED" && typeof window !== "undefined") {
      window.dispatchEvent(new Event(SESSION_ENDED_EVENT));
    }
    if (failure.status >= 500) reportError(failure, { requestId, method, path: failure.path });
    throw failure;
  }

  return payload as T;
}

export const api = {
  get: <T>(path: string) => request<T>(path),
  post: <T>(path: string, body?: unknown) => request<T>(path, { method: "POST", body }),
  patch: <T>(path: string, body?: unknown) => request<T>(path, { method: "PATCH", body }),
  put: <T>(path: string, body?: unknown) => request<T>(path, { method: "PUT", body }),
  // Body is optional and rarely used — DELETE is normally identified by its
  // URL alone — but the bulk endpoints take a request body (a list of ids),
  // since a batch has no single resource path to name.
  delete: <T>(path: string, body?: unknown) => request<T>(path, { method: "DELETE", body }),
};

/* ─────────────────────────── Domain types ─────────────────────────── */

export type Viewer = {
  id: string;
  email: string;
  displayName: string;
  role: "admin" | "manager" | "editor" | "member" | "viewer";
  /** Their profile picture, or null for initials. */
  avatarUrl?: string | null;
  /** True while the account is on a password an administrator issued. */
  mustChangePassword?: boolean;
  /** IANA zone, e.g. "America/New_York". Decides when their digest and due-task nudge arrive. */
  timezone?: string;
  /** Contact number shown on their People profile; null when not set. */
  phone?: string | null;
  /** The office they open into by default, or null for the global default. Only
   *  meaningful for someone in more than one office. */
  defaultWorkspaceId?: string | null;
  /** Whether they have two-factor authentication on. */
  twoFactorEnabled?: boolean;
  /** True when the workspace requires two-factor and they have not enrolled yet. */
  twoFactorSetupRequired?: boolean;
  /** When they finished or skipped the first-run tour; null until they have. */
  tourCompletedAt?: string | null;
};

export type Channel = {
  id: string;
  projectId: string | null;
  name: string;
  topic: string;
  kind: "project" | "team" | "direct" | "group";
  isPrivate: boolean;
  lastMessageAt: string | null;
  /** Longer than the topic; only owners and admins edit it. */
  description?: string;
  /** Set once the channel is archived: read-only and hidden by default. */
  archivedAt?: string | null;
  /** Set while this channel is pinned to the top of the viewer's own sidebar. Personal, not shared. */
  pinnedAt?: string | null;
  unreadCount?: number;
  memberCount?: number;
};

export type Message = {
  id: string;
  channelId: string;
  /** Echoed back so a sender's own optimistic row can be matched by identity
   *  rather than guessed from author+body. Null for anyone else's message. */
  clientMessageId?: string | null;
  authorId: string;
  authorName: string;
  /** "system" is a line the app wrote ("Asha changed the topic"), not a person. */
  kind?: "message" | "system";
  /** The author's custom status, when they have one. Null for no status. */
  authorStatus?: { emoji: string | null; text: string | null; dnd: boolean } | null;
  parentMessageId: string | null;
  /** The quoted message this replies to, resolved server-side. Null when this
   *  is not a reply, or when the parent has since been deleted. */
  parent?: { id: string; authorName: string; body: string } | null;
  body: string;
  editedAt: string | null;
  /** Set when the message was withdrawn; `body` is then empty and the thread
   *  shows a tombstone in its place. */
  deletedAt?: string | null;
  attachmentCount: number;
  /** Files shared with this message, so an image can render in the thread
   *  rather than only as text naming it. `scanStatus: "pending"` means the
   *  virus scan has not finished: shown as "Scanning…", not openable. */
  attachments?: { id: string; fileName: string; contentType: string; sizeBytes: number; scanStatus?: string }[];
  /** Set when this message was created by forwarding a file from elsewhere. */
  isForwarded?: boolean;
  /** The quoted original of a forward. `channelId` is null when the viewer
   *  cannot read the source, so there is nothing to link back to. */
  forwardedFrom?: ForwardedFrom | null;
  /** The first link's preview, fetched by the server after the message was stored. */
  linkPreview?: LinkPreview | null;
  createdAt: string;
  reactions?: { emoji: string; count: number; reacted: boolean }[];
  linkedTask?: {
    id: string;
    title: string;
    status: string;
    priority: string;
    assigneeName: string | null;
    dueAt: string | null;
  } | null;
  /** The viewer's own flag on this message, or null. Never anyone else's. */
  myFlag?: MessageFlagKind | null;
  /** A reply that lives in a thread panel rather than the channel stream. */
  inThread?: boolean;
  /** A thread reply the author also posted to the channel. */
  alsoInChannel?: boolean;
  /** Present on a thread's root: how many replies, and who wrote them. */
  thread?: ThreadSummary | null;
  pinned?: boolean;
  /** Whether the viewer saved this for later. Personal, like `myFlag`. */
  isSaved?: boolean;
};

export type ForwardedFrom = {
  authorName: string;
  channelName: string | null;
  channelId: string | null;
  messageId: string | null;
  quote: string;
};

export type LinkPreview = { url: string; title: string | null; description: string | null; siteName: string | null };

export type ThreadSummary = {
  replyCount: number;
  lastReplyAt: string | null;
  participants: { id: string; displayName: string; avatarUrl: string | null }[];
};

/** Where a flagged message is in its personal lifecycle. */
export type MessageFlagKind = "flag" | "todo" | "done";

/** A personal way of grouping channels in the sidebar — visible only to whoever made it. */
export type ChannelLabel = {
  id: string;
  name: string;
  channelIds: string[];
};

export type Project = {
  id: string;
  code: string;
  name: string;
  summary: string | null;
  status: string;
  health: "on_track" | "at_risk" | "off_track";
  ownerId: string;
  ownerName: string;
  startDate: string | null;
  targetDate: string | null;
  version: number;
  /** Whether the viewer belongs to this project, and in what capacity. */
  isMember: boolean;
  memberRole: string | null;
  /** Counts describe top-level work; subtasks are reported separately so a
   * card cannot disagree with the list it sits above. */
  taskCount: number;
  openTaskCount: number;
  doneTaskCount: number;
  subtaskCount: number;
  /** Set when the project is archived (read-only, hidden from default lists). */
  archivedAt?: string | null;
};

export type TaskStatus = "todo" | "in_progress" | "review" | "done" | "cancelled";
export type TaskPriority = "low" | "normal" | "medium" | "high" | "urgent";

/**
 * One owner of a task, and whether they personally have finished.
 *
 * `completedAt` is per person: on a task owned by three people, "Priya is done"
 * and "the task is done" are different facts, related by the task's
 * `completionMode`.
 */
export type TaskAssignee = {
  userId: string;
  /** Who put this person on the task — used to decide who may delete it. */
  addedById: string;
  displayName: string;
  avatarUrl: string | null;
  completedAt: string | null;
};

/** What "done" means when several people own a task. */
export type CompletionMode = "any" | "all";

/**
 * A task as every read endpoint returns it.
 *
 * The server hydrates one shape for the list, the board, and the detail view,
 * so this mirrors it exactly rather than being a narrower list-only type —
 * two views that disagree about the shape of the same record end up
 * disagreeing about how to render it.
 */
/** A user-defined board column, and the category that carries its semantics. */
export type BoardColumnDef = {
  id: string;
  projectId: string;
  name: string;
  category: "todo" | "in_progress" | "done";
  position: number;
  isDefault: boolean;
};

export type Task = {
  id: string;
  projectId: string | null;
  /** The board column this task sits in, when it belongs to a project. */
  columnId: string | null;
  projectCode: string | null;
  projectName: string | null;
  parentTaskId: string | null;
  blockedByTaskId: string | null;
  title: string;
  description: string;
  status: TaskStatus;
  priority: TaskPriority;
  /**
   * The primary owner — the earliest-added of `assignees`, derived server-side.
   * Kept because most of the app asks "whose task is this?" and wants one
   * answer; `assignees` is the whole truth.
   */
  assigneeId: string | null;
  assigneeName: string | null;
  assigneeAvatarUrl: string | null;
  assignees: TaskAssignee[];
  reporterId: string;
  reporterName: string | null;
  dueAt: string | null;
  startAt: string | null;
  completedAt: string | null;
  estimateMinutes: number | null;
  position: number;
  version: number;
  completionMode: CompletionMode;
  /** An RRULE when this task repeats; null otherwise. Requires a due date. */
  recurrenceRule: string | null;
  /** Set on an instance spawned by completing an earlier one in the series. */
  recurrenceParentId: string | null;
  sourceMessageId: string | null;
  createdAt: string;
  updatedAt: string;
  subtaskCount: number;
  completedSubtaskCount: number;
  commentCount: number;
  /** Blockers that are neither done nor cancelled; above zero the task is waiting on somebody. */
  openBlockerCount?: number;
  /** Ids of this task's blockers that are in the same result: the timeline's arrows. */
  blockedByIds?: string[];
  sectionId?: string | null;
  sectionName?: string | null;
  milestoneId?: string | null;
  milestoneName?: string | null;
  /** Checklist items on the task and how many are ticked: the "3/5" on rows and cards. */
  checklistTotal?: number;
  checklistDone?: number;
  tags?: Tag[];
  /** Project custom field values: field id to value. Absent means not set. */
  customValues?: Record<string, string | number>;
};

export type Tag = { id: string; name: string; color: string; taskCount?: number };

export type MilestoneStatus = "planned" | "at_risk" | "done" | "missed";

export type Milestone = {
  id: string;
  projectId: string;
  name: string;
  description: string | null;
  ownerId: string | null;
  ownerName: string | null;
  dueDate: string;
  status: MilestoneStatus;
  version: number;
  taskCount: number;
  doneCount: number;
  /** 0-100, or null when no countable task is linked. */
  progress: number | null;
};

export type ProjectSection = { id: string; projectId: string; name: string; position: number };

export type ChecklistItem = {
  id: string;
  taskId: string;
  text: string;
  done: boolean;
  assigneeId: string | null;
  assigneeName?: string | null;
  position: number;
};

/**
 * Who may delete a task, mirroring the server's canDeleteTask: an admin, the
 * creator (reporter), or anyone who assigned someone to it. Used to hide the
 * delete action where it would only be refused. The server enforces the same
 * rule regardless — this is UX, not the security boundary.
 */
export function canDeleteTask(task: Task, viewer: Pick<Viewer, "id" | "role">): boolean {
  if (viewer.role === "admin") return true;
  if (task.reporterId === viewer.id) return true;
  return task.assignees.some((a) => a.addedById === viewer.id);
}

export type TaskComment = {
  id: string;
  body: string;
  authorId: string;
  authorName: string;
  authorAvatarUrl: string | null;
  createdAt: string;
};

export type Person = {
  id: string;
  displayName: string;
  email: string;
  department: string | null;
  jobTitle: string | null;
  role: string;
  availability: string;
  lastSeenAt: string | null;
  /** Custom status and Do Not Disturb, as the directory shows them. */
  status?: { emoji: string | null; text: string | null; expiresAt?: string | null; dnd: boolean } | null;
};

/** One bucket of GET /daily-reports/team/:userId/trend — mirrors the API's
 *  response shape exactly, per-week or per-calendar-month depending on the
 *  request's `bucket` param. */
export type TrendBucket = {
  start: string;
  end: string;
  label: string;
  workdays: number;
  reported: number;
  rate: number | null;
};
